/** @typedef {import('../types/compliance').KycRecord} KycRecord */
/** @typedef {import('../types/compliance').KycEntry} KycEntry */
/** @typedef {import('../types/compliance').ComplianceUploadResponse} ComplianceUploadResponse */
/** @typedef {import('../types/compliance').ComplianceSaveResponse} ComplianceSaveResponse */
/** @typedef {import('../types/compliance').ComplianceSubmitResponse} ComplianceSubmitResponse */
/** @typedef {import('../types/compliance').MerchantType} MerchantType */

import { instance } from '../config/api';

/**
 * Initialize a new KYC record for the merchant
 * @param {MerchantType} merchant_type
 * @returns {Promise<KycRecord>}
 */
const initComplianceKyc = async (merchant_type) => {
  const { data } = await instance.post(`kyc/init`, { merchant_type });
  return data?.data;
};

/**
 * Upload a compliance document/attachment
 * @param {FormData} formData - FormData with 'file' field
 * @returns {Promise<ComplianceUploadResponse>}
 */
const uploadComplianceAttachment = async (formData) => {
  const { data } = await instance.post(`kyc/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return data?.data;
};

/**
 * Save or update a section entry
 * @param {string} sectionKey - e.g. 'BUSINESS_DETAILS', 'FREELANCER_BASIC_DETAILS'
 * @param {Object} body - Section data + optional entry_id, version, position
 * @returns {Promise<ComplianceSaveResponse>}
 */
const saveComplianceSection = async (sectionKey, body) => {
  const { data } = await instance.put(`kyc/section/${sectionKey}`, body);
  return data?.data;
};

/**
 * Delete an entry from a multi-entry section
 * @param {string} sectionKey
 * @param {string} entryId
 * @returns {Promise<Object>}
 */
const deleteComplianceEntry = async (sectionKey, entryId) => {
  const { data } = await instance.delete(`kyc/section/${sectionKey}/${entryId}`);
  return data?.data;
};

/**
 * Submit KYC for admin review
 * @param {string} idempotency_key
 * @returns {Promise<ComplianceSubmitResponse>}
 */
const submitComplianceForReview = async (idempotency_key) => {
  const { data } = await instance.post(`kyc/submit-for-review`, { idempotency_key });
  return data?.data;
};

/**
 * Restart (reset) the current KYC record so the merchant can begin fresh
 * @returns {Promise<Object>}
 */
const restartComplianceKyc = async () => {
  const { data } = await instance.post(`kyc/restart`);
  return data?.data;
};

/**
 * Get full KYC profile for merchant dashboard
 * @returns {Promise<KycRecord>}
 */
const getComplianceProfile = async () => {
  const { data } = await instance.get(`kyc`);
  return data?.data;
};

// ─── Admin Endpoints ──────────────────────────────────────────────────────────

/**
 * List KYC records (admin only)
 * @param {{ limit?: number, offset?: number, status?: string, merchant_type?: string }} [params]
 * @returns {Promise<{ data: KycRecord[], meta: { limit: number, offset: number, hasNext: boolean } }>}
 */
const adminListComplianceRecords = async (params = {}) => {
  const { limit = 20, offset = 0, status, merchant_type } = params;
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    ...(status && { status }),
    ...(merchant_type && { merchant_type })
  });

  const { data } = await instance.get(`compliance?${queryParams}`);
  const meta = {
    limit: Number(data?.meta?.limit ?? limit),
    offset: Number(data?.meta?.offset ?? offset),
    hasNext: Boolean(data?.meta?.hasNext)
  };
  return {
    data: data?.data || [],
    meta
  };
};

/**
 * Get full KYC profile for admin review (includes signed S3 URLs)
 * @param {string} merchantId
 * @returns {Promise<KycRecord>}
 */
const adminGetComplianceProfile = async (merchantId) => {
  const { data } = await instance.get(`compliance/${merchantId}`);
  return data?.data;
};

/**
 * Submit field-level review decisions (admin only)
 * @param {{ entry_id: string, field_key: string, status: string, reason?: string }[]} reviews
 * @returns {Promise<Object>}
 */
const adminReviewComplianceFields = async (reviews) => {
  const { data } = await instance.patch(`compliance/review`, { reviews });
  return data?.data;
};

export {
  adminGetComplianceProfile,
  adminListComplianceRecords,
  adminReviewComplianceFields,
  deleteComplianceEntry,
  getComplianceProfile,
  initComplianceKyc,
  restartComplianceKyc,
  saveComplianceSection,
  submitComplianceForReview,
  uploadComplianceAttachment
};
